import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Chip, type ChipVariant } from '../../components/ui/Chip';
import { TableRowSkeleton, Skeleton } from '../../components/ui/Skeleton';
import { CompactEmptyState } from '../../components/ui/EmptyState';
import { useAuthStore } from '../../stores/auth.store';
import { useCampaigns } from '../../api/campaigns.api';
import { useOrgUsers } from '../../api/org-users.api';
import { ownerFor } from '../../lib/people';
import { useReportsSummary, useEngagement, useActivityFeed, useCampaignReports, type ActivityFeedItem, type DailyEngagementPoint } from '../../api/reports.api';
import { useConversations, type ConversationListItem } from '../../api/inbox.api';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/* ── Screen 01 — Main dashboard ── */

function timeGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const firstName = user?.name?.split(' ')[0] ?? 'there';
  const today = new Date().toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  // Unread conversation count drives both the heading text and the
  // "Replies awaiting you" stat card so they stay in lockstep.
  const { data: unreadConvs } = useConversations({ unread: true });
  const inboxCount = unreadConvs?.data?.length ?? 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <HeadingRow firstName={firstName} today={today} inboxCount={inboxCount} />
      <StatCardsRow inboxCount={inboxCount} />
      <TwoColumnLayout />
    </div>
  );
}

/* ── Heading row ── */

function HeadingRow({
  firstName,
  today,
  inboxCount,
}: {
  firstName: string;
  today: string;
  inboxCount: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 'var(--space-4)',
      }}
    >
      <div>
        <h1
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-4xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-brand)',
            letterSpacing: 'var(--tracking-tight)',
            lineHeight: 'var(--leading-tight)',
          }}
        >
          {timeGreeting()}, {firstName}
        </h1>
        <p
          style={{
            marginTop: 'var(--space-1)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--color-text-muted)',
          }}
        >
          {today} · {inboxCount} {inboxCount === 1 ? 'reply' : 'replies'} waiting in your inbox
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <IconButton aria-label="Refresh">
          <IconRefresh size={16} />
        </IconButton>
        <RangeDropdown />
        <Link to="/campaigns/new" style={{ textDecoration: 'none' }}>
          <PrimaryButton>
            <IconPlus size={14} />
            <span style={{ marginLeft: 'var(--space-1)' }}>New Campaign</span>
          </PrimaryButton>
        </Link>
      </div>
    </div>
  );
}

/* ── Stat cards row ── */

interface Stat {
  label: string;
  /// Stringified value — formatting (commas, percent) lives at the call site
  /// so a missing data point can render as "—" rather than "NaN" or "0%".
  value: string;
  subtext: string;
}

function StatCardsRow({ inboxCount }: { inboxCount: number }) {
  // Last-7-days window for the rate / clicks counters. Open rate over a
  // wider window would smooth out daily noise but also hide a sudden drop
  // — a recruiter-facing dashboard wants the latter visible.
  const from = useMemo(() => new Date(Date.now() - SEVEN_DAYS_MS).toISOString(), []);

  const { data: campaignsList, isLoading: loadingCampaigns } = useCampaigns({ status: 'ACTIVE', limit: 1 });
  const { data: summary, isLoading: loadingSummary } = useReportsSummary({ date_from: from });

  const activeCount = campaignsList?.meta?.total ?? 0;
  const openRatePct = summary ? Math.round(summary.open_rate * 100) : null;
  // Calendar clicks (added in the calendar-tracking sprint) is the closest
  // signal we have to "meetings booked" without a JT-side webhook on the
  // actual confirmation. Label says "Calendar clicks" so we don't overclaim.
  const calendarClicks = (summary as { calendar_clicked?: number } | undefined)?.calendar_clicked ?? 0;

  const isLoading = loadingCampaigns || loadingSummary;

  const stats: Stat[] = [
    {
      label: 'Active campaigns',
      value: activeCount.toLocaleString(),
      subtext: 'Includes drafts? No, only campaigns currently sending.',
    },
    {
      label: 'Open rate (7d)',
      value: openRatePct == null ? '-' : `${openRatePct}%`,
      subtext: 'Across all sends in the last 7 days.',
    },
    {
      label: 'Replies awaiting you',
      value: inboxCount.toLocaleString(),
      subtext: inboxCount === 0 ? 'Inbox zero: nice.' : 'Open the inbox to triage.',
    },
    {
      label: 'Calendar clicks (7d)',
      value: calendarClicks.toLocaleString(),
      subtext: 'Recipients who clicked a {{calendar_link}}.',
    },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 'var(--space-4)',
      }}
    >
      {isLoading
        ? Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
        : stats.map((s) => <StatCard key={s.label} stat={s} />)}
    </div>
  );
}

function StatCard({ stat }: { stat: Stat }) {
  return (
    <div
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--stat-card-padding)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <p
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-muted)',
          letterSpacing: 'var(--tracking-caps)',
          textTransform: 'uppercase',
        }}
      >
        {stat.label}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--stat-card-number-size)',
            fontWeight: 'var(--stat-card-number-weight)',
            color: 'var(--color-text-brand)',
            letterSpacing: 'var(--tracking-tight)',
            lineHeight: 'var(--leading-tight)',
          }}
        >
          {stat.value}
        </span>
      </div>

      <p
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-muted)',
        }}
      >
        {stat.subtext}
      </p>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--stat-card-padding)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <Skeleton variant="text" width="55%" />
      <Skeleton variant="title" width="40%" style={{ height: '32px' }} />
      <Skeleton variant="text" width="70%" />
    </div>
  );
}

/* ── Two-column layout ── */

function TwoColumnLayout() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
        gap: 'var(--space-6)',
        alignItems: 'flex-start',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', minWidth: 0 }}>
        <ActiveCampaignsCard />
        <EngagementChartCard />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', minWidth: 0 }}>
        <RepliesPanel />
        <ActivityPanel />
      </div>
    </div>
  );
}

/* ── Active campaigns table ── */

interface CampaignRow {
  id: string;
  name: string;
  subtext: string;
  audience: string;
  audienceCount: number;
  sent: number;
  openRate: number; /* 0-1 */
  replies: number;
  status: ChipVariant;
  statusLabel: string;
  ownerInitials: string;
  ownerName: string;
  ownerColor: string; /* var() reference */
}

// Stable palette for owner avatars — hashed from the owner_user_id so the
// same recruiter always renders in the same colour across views.
const OWNER_PALETTE = [
  'var(--color-brand-indigo)',
  'var(--color-brand-blue)',
  'var(--color-brand-teal)',
  'var(--color-brand-navy)',
  'var(--color-warning)',
  'var(--color-success)',
];

function ownerColorFor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  return OWNER_PALETTE[Math.abs(hash) % OWNER_PALETTE.length];
}

function statusToChip(status: string): { variant: ChipVariant; label: string } {
  switch (status) {
    case 'ACTIVE': return { variant: 'live', label: 'Live' };
    case 'PAUSED': return { variant: 'awaiting', label: 'Paused' };
    case 'DRAFT': return { variant: 'idle', label: 'Draft' };
    case 'COMPLETED': return { variant: 'meeting', label: 'Completed' };
    case 'ARCHIVED': return { variant: 'idle', label: 'Archived' };
    default: return { variant: 'idle', label: status };
  }
}

function ActiveCampaignsCard() {
  // useCampaigns gives us metadata (status, owner_user_id) and useCampaignReports
  // gives us per-campaign metrics. Joined here so the table can show both.
  // Limit 8 — dashboard is a glance, not a full inventory.
  const { data: campaignsList, isLoading: loadingList } = useCampaigns({ status: 'ACTIVE', limit: 8 });
  const { data: reports, isLoading: loadingReports } = useCampaignReports({ limit: 50, sort: 'sent' });
  const { data: orgUsers } = useOrgUsers();

  const rows: CampaignRow[] = useMemo(() => {
    const campaigns = campaignsList?.data ?? [];
    const reportById = new Map((reports ?? []).map((r) => [r.id, r]));
    return campaigns.map((c) => {
      const r = reportById.get(c.id);
      const chip = statusToChip(c.status);
      const owner = ownerFor(c.owner_user_id, orgUsers);
      const sent = r?.metrics.sent ?? 0;
      const replies = r?.metrics.replied ?? 0;
      const openRate = r?.rates.open_rate ?? 0;
      const launchedAgo = c.launched_at
        ? relativeDate(new Date(c.launched_at))
        : c.created_at
          ? `Created ${relativeDate(new Date(c.created_at))}`
          : '';
      return {
        id: c.id,
        name: c.name,
        subtext: launchedAgo,
        // Audience name isn't on the list endpoint — left blank rather than
        // faking one. Detail page shows the audience link.
        audience: '-',
        audienceCount: 0,
        sent,
        openRate,
        replies,
        status: chip.variant,
        statusLabel: chip.label,
        ownerInitials: owner.initials,
        ownerName: owner.name,
        ownerColor: ownerColorFor(c.owner_user_id),
      };
    });
  }, [campaignsList, reports, orgUsers]);

  const isLoading = loadingList || loadingReports;

  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        overflow: 'hidden',
      }}
    >
      <PanelHeader title="Your active campaigns" linkLabel="View all" linkTo="/campaigns" />

      {isLoading && rows.length === 0 ? (
        <div style={{ padding: 'var(--space-4)' }}>
          <TableRowSkeleton rows={4} />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 'var(--space-4)' }}>
          <CompactEmptyState
            icon={<IconSend size={16} />}
            message="No active campaigns yet. Launch one to start tracking results here."
          />
        </div>
      ) : (
        <CampaignsTable rows={rows} />
      )}
    </section>
  );
}

function relativeDate(d: Date): string {
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString();
}

function CampaignsTable({ rows }: { rows: CampaignRow[] }) {
  const navigate = useNavigate();
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: 'var(--font-sans)',
        }}
      >
        <thead>
          <tr>
            <Th>Campaign</Th>
            <Th>Audience</Th>
            <Th align="right">Sent</Th>
            <Th>Open rate</Th>
            <Th align="right">Replies</Th>
            <Th>Status</Th>
            <Th align="right">Owner</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => navigate(`/campaigns/${row.id}`)}
              style={{
                borderTop: '1px solid var(--table-border)',
                transition: 'background-color var(--transition-base)',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--table-row-hover-bg)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <Td>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    {row.name}
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {row.subtext}
                  </span>
                </div>
              </Td>
              <Td>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    {row.audience}
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {row.audienceCount.toLocaleString()} candidates
                  </span>
                </div>
              </Td>
              <Td align="right">
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {row.sent.toLocaleString()}
                </span>
              </Td>
              <Td>
                <InlineRateBar value={row.openRate} />
              </Td>
              <Td align="right">
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {row.replies}
                </span>
              </Td>
              <Td>
                <Chip variant={row.status}>{row.statusLabel}</Chip>
              </Td>
              <Td align="right">
                <Avatar initials={row.ownerInitials} color={row.ownerColor} title={row.ownerName} />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      style={{
        height: 'var(--table-header-height)',
        padding: '0 var(--table-cell-padding-x)',
        textAlign: align,
        backgroundColor: 'var(--table-header-bg)',
        color: 'var(--table-header-text)',
        fontSize: 'var(--table-header-size)',
        fontWeight: 'var(--table-header-weight)',
        letterSpacing: 'var(--table-header-tracking)',
        textTransform: 'uppercase',
        borderBottom: '1px solid var(--table-border)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <td
      style={{
        padding: 'var(--table-cell-padding-y) var(--table-cell-padding-x)',
        textAlign: align,
        verticalAlign: 'middle',
        height: 'var(--table-row-height)',
      }}
    >
      {children}
    </td>
  );
}

function InlineRateBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: '120px' }}>
      <div
        style={{
          flex: 1,
          height: '6px',
          borderRadius: 'var(--radius-full)',
          backgroundColor: 'var(--color-surface-muted)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            backgroundColor: 'var(--color-brand-blue)',
            borderRadius: 'var(--radius-full)',
          }}
        />
      </div>
      <span
        style={{
          fontSize: 'var(--text-xs)',
          fontFamily: 'var(--font-mono)',
          color: 'var(--color-text-secondary)',
          minWidth: '34px',
          textAlign: 'right',
        }}
      >
        {pct}%
      </span>
    </div>
  );
}

/* ── Engagement chart ── */

interface ChartData {
  labels: string[];
  opens: number[];
  replies: number[];
  /** Calendar-link clicks per day — the closest signal we have to
   *  "meetings booked" until JT exposes a confirmation webhook. */
  meetings: number[];
}

function EngagementChartCard() {
  const dateFrom = useMemo(() => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - 13);
    d.setUTCHours(0, 0, 0, 0);
    return d.toISOString();
  }, []);
  const dateTo = useMemo(() => new Date().toISOString(), []);
  const { data: points, isLoading } = useEngagement({ date_from: dateFrom, date_to: dateTo, days: 14 });

  const data: ChartData = useMemo(() => {
    const pts = (points ?? []) as DailyEngagementPoint[];
    return {
      labels: pts.map((p) => formatChartLabel(p.date)),
      opens: pts.map((p) => p.opened),
      replies: pts.map((p) => p.replied),
      // The engagement endpoint returns SENT/OPENED/REPLIED only. Meetings/
      // calendar clicks aren't bucketed daily on the API yet — render zeros
      // so the legend stays consistent with the rest of the dashboard.
      meetings: pts.map(() => 0),
    };
  }, [points]);

  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        padding: 'var(--card-padding)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-brand)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          Engagement · last 14 days
        </h2>
        <ChartLegend />
      </div>

      {isLoading && data.labels.length === 0 ? (
        <Skeleton variant="block" width="100%" style={{ height: '220px' }} />
      ) : data.labels.length === 0 ? (
        <CompactEmptyState
          icon={<IconSend size={16} />}
          message="No engagement yet. Once campaigns start sending, this chart fills in."
        />
      ) : (
        <LineChart data={data} />
      )}
    </section>
  );
}

function formatChartLabel(iso: string): string {
  // The API returns YYYY-MM-DD (already UTC). Render as "Mon DD" — short
  // enough that 14 ticks fit comfortably.
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
}

function ChartLegend() {
  const items = [
    { label: 'Opens', color: 'var(--color-brand-blue)' },
    { label: 'Replies', color: 'var(--color-brand-indigo)' },
    { label: 'Meetings', color: 'var(--color-brand-teal)' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
      {items.map((it) => (
        <span
          key={it.label}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--color-text-muted)',
          }}
        >
          <span
            style={{
              width: '10px',
              height: '10px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: it.color,
            }}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function LineChart({ data }: { data: ChartData }) {
  const width = 720;
  const height = 220;
  const padding = { top: 16, right: 16, bottom: 32, left: 36 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const series = [
    { values: data.opens, color: 'var(--color-brand-blue)' },
    { values: data.replies, color: 'var(--color-brand-indigo)' },
    { values: data.meetings, color: 'var(--color-brand-teal)' },
  ];

  const allValues = series.flatMap((s) => s.values);
  // Guard against an all-zero series (no engagement yet) — without a floor,
  // dividing by 0 below produces NaN in the SVG path and the chart
  // disappears silently.
  const max = Math.max(1, ...allValues);
  // labels.length === 1 → step infinite; cap so the single-point case still renders.
  const step = data.labels.length > 1 ? innerW / (data.labels.length - 1) : innerW;

  const toPath = (values: number[]) =>
    values
      .map((v, i) => {
        const x = padding.left + i * step;
        const y = padding.top + innerH - (v / max) * innerH;
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

  const yTicks = 4;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((max / yTicks) * i));

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
        style={{ display: 'block' }}
      >
        {/* y gridlines */}
        {tickValues.map((tv, i) => {
          const y = padding.top + innerH - (tv / max) * innerH;
          return (
            <g key={i}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={y}
                y2={y}
                stroke="var(--color-border-default)"
                strokeWidth={1}
                strokeDasharray={i === yTicks ? '0' : '2 4'}
              />
              <text
                x={padding.left - 8}
                y={y + 4}
                textAnchor="end"
                fontSize="10"
                fontFamily="var(--font-sans)"
                fill="var(--color-text-subtle)"
              >
                {tv}
              </text>
            </g>
          );
        })}

        {/* lines */}
        {series.map((s, i) => (
          <path key={i} d={toPath(s.values)} fill="none" stroke={s.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        ))}

        {/* dots on the last point of each series */}
        {series.map((s, i) => {
          const last = s.values.length - 1;
          const x = padding.left + last * step;
          const y = padding.top + innerH - (s.values[last] / max) * innerH;
          return <circle key={i} cx={x} cy={y} r={3.5} fill={s.color} stroke="var(--color-surface-white)" strokeWidth={1.5} />;
        })}

        {/* x labels — show every other to avoid crowding */}
        {data.labels.map((label, i) => {
          if (i % 2 !== 0 && i !== data.labels.length - 1) return null;
          const x = padding.left + i * step;
          return (
            <text
              key={i}
              x={x}
              y={height - 10}
              textAnchor="middle"
              fontSize="10"
              fontFamily="var(--font-sans)"
              fill="var(--color-text-subtle)"
            >
              {label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

/* Sparkline removed with the live stat-card rewrite — the cards now show
 * point-in-time values rather than 10-day trend lines. */

/* ── Replies panel ── */

function RepliesPanel() {
  const navigate = useNavigate();
  // Top 4 unread conversations — same data source as the headline counter so
  // the panel agrees with the stat card.
  const { data, isLoading } = useConversations({ unread: true });
  const replies: ConversationListItem[] = (data?.data ?? []).slice(0, 4);

  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        overflow: 'hidden',
      }}
    >
      <PanelHeader title="Replies waiting on you" linkLabel="Open inbox" linkTo="/inbox" />

      {isLoading && replies.length === 0 ? (
        <div style={{ padding: 'var(--space-4)' }}>
          <TableRowSkeleton rows={3} />
        </div>
      ) : replies.length === 0 ? (
        <div style={{ padding: 'var(--space-4)' }}>
          <CompactEmptyState
            icon={<IconInbox size={16} />}
            message="Inbox zero: no replies waiting on you right now."
          />
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {replies.map((conv, i) => {
            const fullName = [conv.contact?.first_name, conv.contact?.last_name]
              .filter(Boolean)
              .join(' ')
              || conv.contact?.email
              || 'Unknown contact';
            const role = [conv.contact?.title, conv.contact?.company].filter(Boolean).join(' · ');
            const initials = (conv.contact?.first_name?.[0] ?? '?')
              + (conv.contact?.last_name?.[0] ?? '');
            return (
              <li
                key={conv.id}
                onClick={() => navigate(`/inbox?thread=${conv.id}`)}
                style={{
                  padding: 'var(--space-4)',
                  borderTop: i === 0 ? 'none' : '1px solid var(--color-border-default)',
                  display: 'flex',
                  gap: 'var(--space-3)',
                  cursor: 'pointer',
                  transition: 'background-color var(--transition-base)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface-overlay)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <Avatar
                  initials={initials.toUpperCase() || '??'}
                  color={ownerColorFor(conv.contact_ref_id)}
                  size="md"
                />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
                    <span
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--color-text-primary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {fullName}
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-text-muted)',
                        flexShrink: 0,
                      }}
                    >
                      {conv.last_message_at ? relativeDate(new Date(conv.last_message_at)) : ''}
                    </span>
                  </div>
                  {role && (
                    <span
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-text-muted)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {role}
                    </span>
                  )}
                  {conv.preview && (
                    <p
                      style={{
                        fontSize: 'var(--text-sm)',
                        color: 'var(--color-text-secondary)',
                        lineHeight: 'var(--leading-snug)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {conv.preview}
                    </p>
                  )}
                  {conv.subject && (
                    <div style={{ marginTop: 'var(--space-1)' }}>
                      <Chip variant="idle">{conv.subject}</Chip>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ── Activity panel ── */

interface ActivityRow {
  id: string;
  icon: React.ReactNode;
  iconBg: string;
  iconFg: string;
  actor: string;
  action: string;
  timestamp: string;
}

interface ActivityVisual {
  icon: React.ReactNode;
  iconBg: string;
  iconFg: string;
  verb: string;
}

function activityVisualFor(eventType: string): ActivityVisual {
  switch (eventType) {
    case 'SENT':
      return { icon: <IconSend size={12} />, iconBg: 'var(--color-blue-50)', iconFg: 'var(--color-brand-blue)', verb: 'sent' };
    case 'DELIVERED':
      return { icon: <IconCheck size={12} />, iconBg: 'var(--color-success-bg)', iconFg: 'var(--color-success)', verb: 'delivered' };
    case 'OPENED':
      return { icon: <IconInbox size={12} />, iconBg: 'var(--color-blue-50)', iconFg: 'var(--color-brand-blue)', verb: 'opened' };
    case 'CLICKED':
      return { icon: <IconArrowUp size={12} />, iconBg: 'var(--color-indigo-50)', iconFg: 'var(--color-brand-indigo)', verb: 'clicked a link in' };
    case 'REPLIED':
      return { icon: <IconInbox size={12} />, iconBg: 'var(--color-success-bg)', iconFg: 'var(--color-success)', verb: 'replied to' };
    case 'BOUNCED':
      return { icon: <IconAlert size={12} />, iconBg: 'var(--color-warning-bg)', iconFg: 'var(--color-warning)', verb: 'bounced from' };
    case 'UNSUBSCRIBED':
      return { icon: <IconAlert size={12} />, iconBg: 'var(--color-warning-bg)', iconFg: 'var(--color-warning)', verb: 'unsubscribed from' };
    case 'SPAM_REPORTED':
      return { icon: <IconAlert size={12} />, iconBg: 'var(--color-warning-bg)', iconFg: 'var(--color-warning)', verb: 'reported as spam' };
    default:
      return { icon: <IconSend size={12} />, iconBg: 'var(--color-surface-muted)', iconFg: 'var(--color-text-muted)', verb: eventType.toLowerCase() };
  }
}

function activityRowFor(item: ActivityFeedItem): ActivityRow {
  const v = activityVisualFor(item.event_type);
  const campaignName = item.campaign_name ? `“${item.campaign_name}”` : 'a campaign';
  const actor = item.contact_email ?? 'A contact';
  return {
    id: item.id,
    icon: v.icon,
    iconBg: v.iconBg,
    iconFg: v.iconFg,
    actor,
    action: `${v.verb} ${campaignName}`,
    timestamp: relativeDate(new Date(item.occurred_at)),
  };
}

function ActivityPanel() {
  const { data, isLoading } = useActivityFeed({ limit: 8 });
  const items = useMemo(
    () => (data ?? []).map(activityRowFor),
    [data]
  );

  return (
    <section
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        overflow: 'hidden',
      }}
    >
      <PanelHeader title="Recent activity" linkLabel="Audit log" linkTo="/settings" />

      {isLoading && items.length === 0 ? (
        <div style={{ padding: 'var(--space-4)' }}>
          <TableRowSkeleton rows={4} />
        </div>
      ) : items.length === 0 ? (
        <div style={{ padding: 'var(--space-4)' }}>
          <CompactEmptyState
            icon={<IconClock size={16} />}
            message="No activity yet: sends, opens, and replies will appear here."
          />
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((a, i) => (
            <li
              key={a.id}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                borderTop: i === 0 ? 'none' : '1px solid var(--color-border-default)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  borderRadius: 'var(--radius-full)',
                  backgroundColor: a.iconBg,
                  color: a.iconFg,
                  flexShrink: 0,
                  marginTop: '2px',
                }}
              >
                {a.icon}
              </span>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <p
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-secondary)',
                    lineHeight: 'var(--leading-snug)',
                  }}
                >
                  <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
                    {a.actor}
                  </span>{' '}
                  {a.action}
                </p>
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  {a.timestamp}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ── Shared building blocks ── */

function PanelHeader({ title, linkLabel, linkTo }: { title: string; linkLabel: string; linkTo: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-4) var(--space-5)',
        borderBottom: '1px solid var(--color-border-default)',
      }}
    >
      <h2
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-brand)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        {title}
      </h2>
      <Link
        to={linkTo}
        style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-link)',
          textDecoration: 'none',
        }}
      >
        {linkLabel} →
      </Link>
    </div>
  );
}

function PrimaryButton({ children }: { children: React.ReactNode }) {
  return (
    <button
      type="button"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-primary)',
        color: 'var(--color-action-primary-text)',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color var(--transition-base)',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-action-primary-hover)')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-action-primary)')}
    >
      {children}
    </button>
  );
}

function IconButton({ children, 'aria-label': ariaLabel }: { children: React.ReactNode; 'aria-label': string }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'var(--btn-height)',
        height: 'var(--btn-height)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-secondary)',
        color: 'var(--color-text-muted)',
        border: '1px solid var(--color-action-secondary-border)',
        cursor: 'pointer',
        transition: 'background-color var(--transition-base), color var(--transition-base)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--color-action-secondary-hover)';
        e.currentTarget.style.color = 'var(--color-text-primary)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--color-action-secondary)';
        e.currentTarget.style.color = 'var(--color-text-muted)';
      }}
    >
      {children}
    </button>
  );
}

function RangeDropdown() {
  return (
    <button
      type="button"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-secondary)',
        color: 'var(--color-text-primary)',
        border: '1px solid var(--color-action-secondary-border)',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--font-weight-medium)',
        fontFamily: 'var(--font-sans)',
        cursor: 'pointer',
        transition: 'background-color var(--transition-base)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-action-secondary-hover)')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-action-secondary)')}
    >
      <IconCalendar size={14} />
      Last 7 days
      <IconChevronDown size={12} />
    </button>
  );
}

function Avatar({
  initials,
  color,
  size = 'sm',
  title,
}: {
  initials: string;
  color: string;
  size?: 'sm' | 'md';
  /** Full name, shown on hover. */
  title?: string;
}) {
  const dim = size === 'md' ? 'var(--avatar-size-md)' : 'var(--avatar-size-sm)';
  const font = size === 'md' ? 'var(--avatar-font-size-md)' : 'var(--avatar-font-size-sm)';
  return (
    <span
      title={title}
      aria-label={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: dim,
        height: dim,
        borderRadius: 'var(--avatar-radius)',
        backgroundColor: color,
        color: 'var(--color-text-inverse)',
        fontFamily: 'var(--font-sans)',
        fontSize: font,
        fontWeight: 'var(--font-weight-semibold)',
        letterSpacing: 'var(--tracking-tight)',
        flexShrink: 0,
      }}
    >
      {initials}
    </span>
  );
}

/* ── Icons (Feather) ── */

function IconRefresh({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function IconPlus({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function IconArrowUp({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function IconChevronDown({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function IconCalendar({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function IconSend({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function IconInbox({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

function IconClock({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function IconCheck({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IconAlert({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
